import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SysParameter } from '@core/models/business/sys-parameter.model';
import { RiskPermission } from '@core/models/risk-permission.model';
import { StaticPermission } from '@core/models/static-permission.model';
import { AppState } from '@core/store/app.state';
import { setInterplanPermissions, setStaticPermissions, setRiskPermissions, setActionPermissions } from '@core/store/permissions/permissions.action';
import { selectPermissionsState } from '@core/store/permissions/permissions.reducer';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { intersectionWith, isEqual } from 'lodash-es';
import { CammsClientStorageService } from 'projects/camms-client-storage/src/public-api';
import { BehaviorSubject, filter, firstValueFrom, lastValueFrom, Observable, Subscription } from 'rxjs';
import { ApiBaseService } from './api-base.service';
import { ActionPermission } from '@core/models/action-permission.model';
import { ServicePermission } from '@core/models/service-permission.model';
import { setServicePermissions } from '@core/store/permissions/permissions.action';
import { ApplicationConfiguration } from 'src/app/camms-risk/models/application-configuration/application-configuration';
import { selectAppConfigState } from '@core/store/application-configuration/application-configuration.reducer';
import { AppconfigService } from './appconfig.service';
import { setApplicationConfigurations } from '@core/store/application-configuration/application-configuration.action';
import { LoginService } from '@core/auth/services/login.service';
import { InterplanPermission } from '@core/models/interplan-permission.model';
import { appCodes } from '@core/constants/app-codes.constant';
import permissionConfigs from '@assets/permission/user-permissions.json';

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    staticPermissions: { [key: string]: boolean } = {};
    riskPermissions: { [key: string]: boolean } = {};
    actionPermissions: { [key: string]: boolean } = {};
    actionRoPermissions: { [key: string]: boolean } = {};
    servicePermissions: { [key: string]: boolean } = {};
    interplanPermissions: { [key: string]: boolean } = {};
    riskTypes: number[] = [];
    riskRegisters: number[] = [];
    isRiskRO: boolean = false;
    isRiskSRO: boolean = false;
    isConfidentialStaff: boolean = false;
    isCustomActionPermission: boolean = false;
    public isPreparer: boolean;
    public isSubmitter: boolean;
    public isNextApprover: boolean;
    public isNextRejector: boolean;
    public isReSubmitter: boolean;
    public isPreviousApprover: boolean;
    public isPreviousRejector: boolean;
   appConfig: ApplicationConfiguration;
   subscriptions: Subscription;
   private appConfigSubject = new BehaviorSubject<ApplicationConfiguration | null>(null);

    constructor(
        private apiBaseService: ApiBaseService,
        private store: Store<AppState>,
        private storageService: CammsClientStorageService,
        private httpClient: HttpClient,
        private appConfigService: AppconfigService,
        private loginService: LoginService,
    ) {
       this.subscriptions = new Subscription();

       this.subscriptions.add(
           this.store.select(selectPermissionsState).subscribe((perm) => {
               this.staticPermissions = perm.staticPermissions.staticAbilities;
               this.riskPermissions = perm.riskPermissions.riskAbilities;
               this.actionPermissions = perm.actionPermissions.actionAbilities;
               this.actionRoPermissions = perm.actionPermissions.actionRoAbilities;
               this.riskTypes = perm.staticPermissions.riskTypes;
               this.riskRegisters = perm.staticPermissions.riskRegisterIds;
               this.isRiskRO = perm.riskPermissions.assignedPermission.isRO;
               this.isRiskSRO = perm.riskPermissions.assignedPermission.isSRO;
               this.isConfidentialStaff =
                   perm.riskPermissions.assignedPermission.isConfidentialStaff;
   
               this.isPreparer =
                   perm?.riskPermissions?.approvalAssignedPermission?.isPreparer;
               this.isSubmitter =
                   perm?.riskPermissions?.approvalAssignedPermission?.isSubmitter;
               this.isNextApprover =
                   perm?.riskPermissions?.approvalAssignedPermission?.isNextApprover;
               this.isNextRejector =
                   perm?.riskPermissions?.approvalAssignedPermission?.isNextRejector;
               this.isReSubmitter =
                   perm?.riskPermissions?.approvalAssignedPermission?.isReSubmitter;
               this.isPreviousApprover =
                   perm?.riskPermissions?.approvalAssignedPermission?.isPreviousApprover;
               this.isPreviousRejector =
                   perm?.riskPermissions?.approvalAssignedPermission?.isPreviousRejector;
   
               this.servicePermissions = perm.servicePermissions.coreDataAbilities;
               this.interplanPermissions = perm.interplanPermissions.interplanAbilities;
           })
        );


        // Initialize app config subscription
        this.getAppConfig();
    }

    getUserStaticPermissions(): Observable<StaticPermission> {
        return this.apiBaseService.get<StaticPermission>(
            ['Permission/GetUserStaticPermissions'],
            {
                isLoading: false,
            },
        );
    }

    getUserRiskPermissions(
        riskTypeId: number,
        riskAssessmentDetailId?: number | null,
    ): Observable<RiskPermission> {
        const params = new HttpParams()
            .set('RiskTypeId', riskTypeId?.toString())
            .set('AssessmentDetailId', riskAssessmentDetailId?.toString() ?? 0);
        return this.apiBaseService.get<RiskPermission>(
            ['Permission/GetUserRiskPermissions'],
            {
                params: params,
            },
        );
    }

    getUserActionPermissions(
        riskTypeId: number,
        riskAssessmentDetailId?: number | null,
    ): Observable<ActionPermission> {
        const params = new HttpParams()
            .set('RiskTypeId', riskTypeId?.toString())
            .set('AssessmentDetailId', riskAssessmentDetailId?.toString() ?? 0);

        return this.apiBaseService.get<ActionPermission>(
            ['Permission/GetUserActionPermissions'],
            {
                params: params,
                isLoading: true,
            },
        );
    }

    getUserServicePermissions(
    ): Observable<ServicePermission> {
        return this.apiBaseService.get<ServicePermission>(
            ['Permission/GetUserServicePermissions'],
            {
                isLoading: true,
            },
        );
    }

    getUserInterplanPermissions(
    ): Observable<InterplanPermission> {
        return this.apiBaseService.get<InterplanPermission>(
            ['Permission/GetUserInterplanPermissions'],
            {
                isLoading: true,
            },
        );
    }

    deleteCommonActionPermissionsCache() {
        return this.httpClient.delete(`${environment.commonActionApi}/api/v1/Permissions/DeleteStandardRoleCache`);
    }

    getStaticPermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn
            ? this.staticPermissions[abilityName]
            : true;
        return perm ? true : false;
    }

    getRiskPermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn ? this.riskPermissions[abilityName] : true;
        return perm ? true : false;
    }

    getActionPermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn
            ? this.actionPermissions[abilityName]
            : true;
        return perm ? true : false;
    }

    getServicePermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn ? this.servicePermissions[abilityName] : true;
        return perm ? true : false;
    }

    getInterplanPermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn ? this.interplanPermissions[abilityName] : true;
        return perm ? true : false;
    }

   async getRevampedIncidentPermission(abilityName: string): Promise<boolean> {
    if (!environment.isPermissionOn) return true;

       //Wait until settings is ready
        const config = await firstValueFrom(
            this.appConfigSubject.pipe(
            filter((c): c is ApplicationConfiguration => !!c?.settings)
            )
        );

        return !!config.settings?.[abilityName];
    }

    getActionRoPermission(abilityName: string): boolean {
        const isPermissionOn = environment.isPermissionOn;

        const perm = isPermissionOn
            ? this.actionRoPermissions[abilityName]
            : true;
        return perm ? true : false;
    }

    async setStaticPermissionsToState(): Promise<void> {
        return new Promise(async (res) => {
            if (Object.keys(this.staticPermissions).length > 0) {
                res();
                return;
            }

            let existingPerms = JSON.parse(
                this.storageService.getData(appCodes.auth.staticPermissions) ||
                    '{}',
            ) as StaticPermission;

            if (existingPerms && Object.keys(existingPerms).length > 0) {
                this.store.dispatch(
                    setStaticPermissions({ staticPermissions: existingPerms }),
                );
            } else if (Object.keys(this.staticPermissions).length == 0) {
                const getPermission$ = this.getUserStaticPermissions();
                let perm = await lastValueFrom(getPermission$);
                this.storageService.setData(
                    appCodes.auth.staticPermissions,
                    perm,
                );
                this.store.dispatch(
                    setStaticPermissions({ staticPermissions: perm }),
                );
            }
            res();
        });
    }

    async setCustomPermissionsToState(
        permissions: StaticPermission,
    ): Promise<void> {
        return new Promise(async (res) => {
            this.store.dispatch(
                setStaticPermissions({ staticPermissions: permissions }),
            );
            res();
        });
    }

    getIsStaticPermissionsLoaded() {
        if (
            (environment.isPermissionOn &&
                this.staticPermissions &&
                Object.keys(this.staticPermissions).length != 0) ||
            !environment.isPermissionOn
        ) {
            return true;
        } else {
            return false;
        }
    }

    getRiskTypeAddPermissions(riskType: number[]) {
        let common = intersectionWith(this.riskTypes, riskType, isEqual);
        return common.length > 0;
    }

    getRiskRegisterViewPermission(registerIds: number[]) {
        let common = intersectionWith(this.riskRegisters, registerIds, isEqual);
        return common.length > 0;
    }

    public isUserPermissionAvailable(permissionId: string): boolean {
        const permissionCode = permissionConfigs[permissionId];
        

        if (!permissionCode) {
            return false;
        }

        const loadedPermissionList = this.getPermissionCodeList();
        return loadedPermissionList?.some((p) => p['name'] == permissionCode);
    }

    public isSettingAvailable(settingId: string): boolean {
        const loadedSettingList = this.getSettingCodeList();

        return loadedSettingList?.some((p) => p['settingName'] == settingId);
    }

    public getSystemParameterValue<T>(parameterName: string): T {
        var jsonString: string = this.storageService.getData(
            appCodes.auth.systemParameterList,
        )!;
        let paramValue: any;

        let parameterList: Array<SysParameter> = JSON.parse(
            JSON.stringify(jsonString),
        );

        for (var i = 0; i < parameterList.length; i++) {
            if (parameterList[i].parameterName === parameterName) {
                paramValue = parameterList[i].data!;
                break;
            }
        }

        return paramValue as T;
    }

    public getPermissionCodeList(): Array<string> {
        const jsonString = this.storageService.getData(
            appCodes.auth.permittedTaskList,
        )!;
        const loadedPermissionList1 = JSON.parse(JSON.stringify(jsonString));
        const loadedPermissionList: Array<string> = JSON.parse(
            loadedPermissionList1,
        );
        return loadedPermissionList;
    }

    public getSettingCodeList(): Array<string> {
        const jsonString = this.storageService.getData(
            appCodes.auth.settingsList,
        );

        if (typeof jsonString === 'string') {
            const loadedSettingList = JSON.parse(jsonString);

            if (typeof loadedSettingList === 'string') {
                return JSON.parse(loadedSettingList) as Array<string>;
            }

            return loadedSettingList;
        }
        return [];
    }

    checkActionPermissionAbility(isActionRo: boolean, abilityName: any) {
        let actionPermission = this.getActionPermission(abilityName);
        if (isActionRo) {
            let actionRoPermission = this.getActionRoPermission(abilityName);
            return actionPermission || actionRoPermission;
        } else {
            return actionPermission;
        }
    }
    
    async setServicePermissionsToState(): Promise<void> {
        const existingPermissions = JSON.parse(
            this.storageService.getData(appCodes.auth.servicePermissions) || '{}',
        );
    
        if (existingPermissions && Object.keys(existingPermissions).length > 0) {
            this.store.dispatch(setServicePermissions({ servicePermissions: existingPermissions }));
        } else {
            const servicePermissions$ = this.getUserServicePermissions();
            const permissions = await lastValueFrom(servicePermissions$);
            this.storageService.setData(appCodes.auth.servicePermissions, permissions);
            this.store.dispatch(setServicePermissions({ servicePermissions: permissions })); 
        }
    }

    async setInterplanPermissionsToState(): Promise<void> {
        const existingPermissions = JSON.parse(
            this.storageService.getData(appCodes.auth.interplanPermissions) || '{}',
        );
    
        if (existingPermissions && Object.keys(existingPermissions).length > 0) {
            this.store.dispatch(setInterplanPermissions({ interplanPermissions: existingPermissions }));
        } else {
            const interplanPermissions$ = this.getUserInterplanPermissions();
            const permissions = await lastValueFrom(interplanPermissions$);
            this.storageService.setData(appCodes.auth.interplanPermissions, permissions);
            this.store.dispatch(setInterplanPermissions({ interplanPermissions: permissions }));
        }
    }

    getAppConfig() {
        this.subscriptions.add(
            this.store.select(selectAppConfigState).subscribe((data) => {
            if (data?.settings) {
                this.appConfig = data;
                this.appConfigSubject.next(data); //Emit the value from store
            } else {
                if(this.loginService.isAuthenticated(true)){
                    this.appConfigService.getApplicationConfiguration().subscribe((config) => {
                    this.appConfig = config;
                    this.store.dispatch(setApplicationConfigurations({ appConfig: config }));
                    this.appConfigSubject.next(config); //Emit the value after API fetch
                    });
                }
            }
            }),
        );
     }

     generatePermissionObject(canView: boolean, canEdit: boolean, requestType: string): any {
        let SettingPermissions = {} as StaticPermission;
        let riskPermissions = {} as RiskPermission;
        let actionPermission = {} as ActionPermission;
        
        if (requestType == 'riskSetting') {
            if (canView && canEdit) {
                SettingPermissions = {
                    staticAbilities: {
                        CanViewRiskFrameworkRiskSetting: true,
                        CanEditRiskFrameworkRiskSetting: true,
                    },
                    riskRegisterIds: [],
                    riskTypes: [],
                };
            } else if (canView) {
                SettingPermissions = {
                    staticAbilities: {
                        CanViewRiskFrameworkRiskSetting: true,
                        CanEditRiskFrameworkRiskSetting: false,
                    },
                    riskRegisterIds: [],
                    riskTypes: [],
                };
            } else {
                SettingPermissions = {
                    staticAbilities: {
                        CanViewRiskFrameworkRiskSetting: false,
                        CanEditRiskFrameworkRiskSetting: false,
                    },
                    riskRegisterIds: [],
                    riskTypes: [],
                };
            }
            this.setCustomPermissionsToState(SettingPermissions);
            return SettingPermissions;
        } else if (requestType == 'riskDetail') {
            if (canView && canEdit) {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: true,
                        CanAddRiskSolution: true,
                        CanEditRiskSolution: true,
                        CanEditStartDateRiskSolution: true,
                        CanEditEndDateRiskSolution: true,
                        CanDeleteRiskSolution: true,
                        CanLinkRiskSolution: true,
                        CanAddRiskControl : true,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                        CanAddRiskControl : false,
                    },
                };
                riskPermissions = {
                    riskAbilities: {
                        CanAddRisk: true,
                        CanDeleteRisk: true,
                        CanViewConfidentialRisk: true,
                        CanViewInherent: true,
                        CanEditInherent: true,
                        CanViewInherentHistory: true,
                        CanEditActiveStatusInherent: true,
                        CanEditRiskAppetiteBenchmarkInherent: true,
                        CanAddNewCausesBowTieInherent: true,
                        CanAddNewConsequencesBowTieInherent: true,
                        CanViewResidual: true,
                        CanEditResidual: true,
                        CanViewResidualHistory: true,
                        CanViewTarget: true,
                        CanEditTarget: true,
                        CanViewTargetHistory: true,
                        CanViewRiskReview: true,
                        CanEditRiskReview: true,
                        CanEditRiskNextReviewDate: true,
                        CanViewRiskReviewHistory: true,
                        CanViewDocuments: true,
                        CanEditDocuments: true,
                        CanViewDocumentsHistory: true,
                        CanViewLinks: true,
                        CanEditLinks: true,
                        CanViewLinksHistory: true,
                        CanViewRiskReviewApprover: true,
                        CanViewRiskReviewSuperApprover: true,
                        CanCreateSuperApprover: true,
                    },
                    assignedPermission: {
                        isRO: false,
                        isSRO: false,
                        isConfidentialStaff: false,
                    },
                    approvalAssignedPermission: {
                        isNextApprover: false,
                        isSubmitter: false,
                        isNextRejector: false,
                        isReSubmitter: false,
                        isPreparer: false,
                        isPreviousApprover: false,
                        isPreviousRejector: false,
                    },
                };
            } else if (canView) {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: true,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                        CanAddRiskControl : false,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                        CanAddRiskControl : false,
                    },
                };
                riskPermissions = {
                    riskAbilities: {
                        CanAddRisk: false,
                        CanDeleteRisk: false,
                        CanViewConfidentialRisk: true,
                        CanViewInherent: true,
                        CanEditInherent: false,
                        CanViewInherentHistory: true,
                        CanEditActiveStatusInherent: false,
                        CanEditRiskAppetiteBenchmarkInherent: false,
                        CanAddNewCausesBowTieInherent: false,
                        CanAddNewConsequencesBowTieInherent: false,
                        CanViewResidual: true,
                        CanEditResidual: false,
                        CanViewResidualHistory: true,
                        CanViewTarget: true,
                        CanEditTarget: false,
                        CanViewTargetHistory: true,
                        CanViewRiskReview: true,
                        CanEditRiskReview: false,
                        CanEditRiskNextReviewDate: false,
                        CanViewRiskReviewHistory: true,
                        CanViewDocuments: true,
                        CanEditDocuments: false,
                        CanViewDocumentsHistory: true,
                        CanViewLinks: true,
                        CanEditLinks: false,
                        CanViewLinksHistory: true,
                        CanViewRiskReviewApprover: false,
                        CanViewRiskReviewSuperApprover: false,
                        CanCreateSuperApprover: false,
                    },
                    assignedPermission: {
                        isRO: false,
                        isSRO: false,
                        isConfidentialStaff: false,
                    },
                    approvalAssignedPermission: {
                        isNextApprover: false,
                        isSubmitter: false,
                        isNextRejector: false,
                        isReSubmitter: false,
                        isPreparer: false,
                        isPreviousApprover: false,
                        isPreviousRejector: false,
                    },
                };
            } else {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                        CanAddRiskControl : false,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                        CanAddRiskControl : false,
                    },
                };
                riskPermissions = {
                    riskAbilities: {
                        CanAddRisk: false,
                        CanDeleteRisk: false,
                        CanViewConfidentialRisk: false,
                        CanViewInherent: false,
                        CanEditInherent: false,
                        CanViewInherentHistory: false,
                        CanEditActiveStatusInherent: false,
                        CanEditRiskAppetiteBenchmarkInherent: false,
                        CanAddNewCausesBowTieInherent: false,
                        CanAddNewConsequencesBowTieInherent: false,
                        CanViewResidual: false,
                        CanEditResidual: false,
                        CanViewResidualHistory: false,
                        CanViewTarget: false,
                        CanEditTarget: false,
                        CanViewTargetHistory: false,
                        CanViewRiskReview: false,
                        CanEditRiskReview: false,
                        CanEditRiskNextReviewDate: false,
                        CanViewRiskReviewHistory: false,
                        CanViewDocuments: false,
                        CanEditDocuments: false,
                        CanViewDocumentsHistory: false,
                        CanViewLinks: false,
                        CanEditLinks: false,
                        CanViewLinksHistory: false,
                        CanViewRiskReviewApprover: false,
                        CanViewRiskReviewSuperApprover: false,
                        CanCreateSuperApprover: false,
                    },
                    assignedPermission: {
                        isRO: false,
                        isSRO: false,
                        isConfidentialStaff: false,
                    },
                    approvalAssignedPermission: {
                        isNextApprover: false,
                        isSubmitter: false,
                        isNextRejector: false,
                        isReSubmitter: false,
                        isPreparer: false,
                        isPreviousApprover: false,
                        isPreviousRejector: false,
                    },
                };
            }
            this.store.dispatch(
                setRiskPermissions({ riskPermissions: riskPermissions }),
            );

            this.store.dispatch(
                setActionPermissions({ actionPermissions: actionPermission }),
            );
            return riskPermissions;
        } else if (requestType == 'actionDetail') {
            this.isCustomActionPermission = true;
            if (canView && canEdit) {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: true,
                        CanAddRiskSolution: true,
                        CanEditRiskSolution: true,
                        CanEditStartDateRiskSolution: true,
                        CanEditEndDateRiskSolution: true,
                        CanDeleteRiskSolution: true,
                        CanLinkRiskSolution: true,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                    },
                };
            } else if (canView) {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: true,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                    },
                };
            } else {
                actionPermission = {
                    actionAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                    },
                    actionRoAbilities: {
                        CanViewRiskSolution: false,
                        CanAddRiskSolution: false,
                        CanEditRiskSolution: false,
                        CanEditStartDateRiskSolution: false,
                        CanEditEndDateRiskSolution: false,
                        CanDeleteRiskSolution: false,
                        CanLinkRiskSolution: false,
                    },
                };
            }
            this.store.dispatch(
                setActionPermissions({ actionPermissions: actionPermission }),
            );
            return actionPermission;
        }
    }
    unsubscribeAll() {
        this.subscriptions.unsubscribe();
    }
}
